import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaPhoneAlt, FaEnvelope, FaMapMarkerAlt, FaArrowRight } from "react-icons/fa";
import { CheckCircle2 } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { addEnquiry, resetEnquiry } from "../../slice/enquirySlice";

const YatraExpertsSection = () => {
  const dispatch = useDispatch();
  const { success: submitted, loading: submitting } = useSelector((state) => state.enquiry);

  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    city: '',
    travelers: '',
    arrivalDate: '',
    requirements: ''
  });
  
  const [errors, setErrors] = useState({});

  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    if (submitted) {
      // Clear local form immediately so it's fresh when success screen disappears
      setForm({ name: '', phone: '', email: '', city: '', travelers: '', arrivalDate: '', requirements: '' });
      
      const timer = setTimeout(() => {
        dispatch(resetEnquiry());
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [submitted, dispatch]);

  const validate = () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = 'Required';
    
    if (!form.phone.trim()) newErrors.phone = 'Required';
    else if (!/^\d{10}$/.test(form.phone)) newErrors.phone = 'Invalid 10-digit number';
    
    if (!form.email.trim()) newErrors.email = 'Required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) newErrors.email = 'Invalid email';

    if (!form.city.trim()) newErrors.city = 'Required';
    if (!form.travelers.trim()) newErrors.travelers = 'Required';
    if (!form.arrivalDate.trim()) newErrors.arrivalDate = 'Required';
    if (!form.requirements.trim()) newErrors.requirements = 'Required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      name: form.name,
      email: form.email,
      number: form.phone,
      city: form.city,
      travelers: parseInt(form.travelers),
      travelDate: form.arrivalDate,
      message: `${form.requirements}`
    };

    dispatch(addEnquiry(payload));
  };

  return (
    <section className="bg-white py-16 lg:py-20 relative overflow-hidden border-t border-gray-50">
      {/* Background Decorative Elements */}
      <div className="absolute top-0 right-0 w-1/4 h-full bg-orange-main/5 -skew-x-12 translate-x-1/3 pointer-events-none" />
      
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          
          {/* LEFT CONTENT */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center gap-2 mb-4">
              <div className="h-[2px] w-8 bg-orange-main" />
              <span className="text-[10px] font-black uppercase tracking-[0.2em] text-orange-main">Contact Us</span>
            </div>
            
            <h2 className="text-4xl lg:text-5xl font-black text-gray-dark tracking-tighter leading-tight mb-6">
              Planning Your Next <br />
              <span className="text-orange-main">Divine Journey?</span>
            </h2>
            
            <p className="text-gray-500 text-base leading-relaxed mb-10 max-w-md font-medium">
              Our Yatra experts are here to help you plan a peaceful and well-organized spiritual tour. Reach out to us for bookings and inquiries.
            </p>
            
            {/* CONTACT INFO LIST */}
            <div className="space-y-4 mb-10">
                <div className="flex items-center gap-4 group">
                <div className="w-12 h-12 rounded-xl bg-white shadow-lg flex items-center justify-center text-orange-main text-lg group-hover:bg-orange-main group-hover:text-white transition-all duration-500">
                  <FaPhoneAlt />
                </div>
                <div>
                  <p className="text-[9px] font-black uppercase tracking-widest text-gray-400 mb-0.5">Call Support</p>
                  <div className="flex flex-col">
                    <a href="tel:+919993910094" className="font-bold text-gray-dark text-base hover:text-orange-main transition-colors">+91 99939 10094</a>
                    <a href="tel:+919981980094" className="font-bold text-gray-dark text-base hover:text-orange-main transition-colors">+91 99819 80094</a>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4 group">
                <div className="w-12 h-12 rounded-xl bg-white shadow-lg flex items-center justify-center text-orange-main text-lg group-hover:bg-orange-main group-hover:text-white transition-all duration-500">
                  <FaEnvelope />
                </div>
                <div>
                  <p className="text-[9px] font-black uppercase tracking-widest text-gray-400 mb-0.5">Email Address</p>
                  <p className="font-bold text-gray-dark text-base">khajurahoroutes@gmail.com</p>
                </div>
              </div>
              <div className="flex items-start gap-4 group">
                
                <div className="w-12 h-12 rounded-xl bg-white shadow-lg flex items-center justify-center text-orange-main text-lg transition-all duration-500">
                  <FaMapMarkerAlt />
                </div>
             <div>
  <p className="text-[9px] font-black uppercase tracking-widest text-gray-400 mb-1">
    Our Offices
  </p>

  <div className="space-y-3">

    {/* KHAJURAHO */}
    <div>
      <p className="font-bold text-gray-dark text-[13px]">
        Khajuraho Office
      </p>

      <p className="text-gray-500 text-[12px] leading-relaxed">
        Opp. Shilpgram, Near Poddar Chowk,
        Khajuraho, Chhatarpur - 471606,
        Madhya Pradesh
      </p>
    </div>

    {/* BHOPAL */}
    <div>
      <p className="font-bold text-gray-dark text-[13px]">
        Bhopal Office
      </p>

      <p className="text-gray-500 text-[12px] leading-relaxed">
        F - 49, First Floor,
        Laxmibai Parisar, Neelbad,
        Bhopal - 462044
      </p>
    </div>

    {/* DELHI */}
    <div>
      <p className="font-bold text-gray-dark text-[13px]">
        Delhi Office
      </p>

      <p className="text-gray-500 text-[12px] leading-relaxed">
        G 20 Palika Palace,
        Panchkuian Road,
        Near R K Ashram Marg Metro Station,
        New Delhi - 110001
      </p>
    </div>

    {/* VARANASI */}
    <div>
      <p className="font-bold text-gray-dark text-[13px]">
        Varanasi Office
      </p>

      <p className="text-gray-500 text-[12px] leading-relaxed">
        Flat No. AF - 2/8,
        Imlak Colony, Phase - 2,
        Nadesar, Varanasi - 221002
      </p>
    </div>

  </div>
</div>
              </div>

            </div>
          </motion.div>

          {/* RIGHT FORM */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="bg-white rounded-[2.5rem] p-8 lg:p-10 shadow-[0_20px_80px_rgba(0,0,0,0.06)] border border-gray-50 relative"
          >
            <AnimatePresence mode="wait">
              {!submitted ? (
                <motion.form
                  key="form"
                  initial={{ opacity: 1 }}
                  exit={{ opacity: 0, y: -10 }}
                  onSubmit={handleSubmit}
                  className="space-y-4"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-[9px] font-black uppercase tracking-widest text-gray-400 ml-3 flex justify-between">
                        <span>Full Name</span>
                        {errors.name && <span className="text-red-500">{errors.name}</span>}
                      </label>
                      <input 
                        type="text" 
                        placeholder="Enter your name"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className={`w-full px-6 py-4 rounded-xl bg-gray-50 border-none transition-all font-medium placeholder:text-gray-300 text-sm outline-none ${
                          errors.name ? 'ring-2 ring-red-400' : 'focus:ring-2 focus:ring-orange-main/20'
                        }`}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[9px] font-black uppercase tracking-widest text-gray-400 ml-3 flex justify-between">
                        <span>Email Address</span>
                        {errors.email && <span className="text-red-500">{errors.email}</span>}
                      </label>
                      <input 
                        type="email" 
                        placeholder="example@mail.com"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        className={`w-full px-6 py-4 rounded-xl bg-gray-50 border-none transition-all font-medium placeholder:text-gray-300 text-sm outline-none ${
                          errors.email ? 'ring-2 ring-red-400' : 'focus:ring-2 focus:ring-orange-main/20'
                        }`}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-[9px] font-black uppercase tracking-widest text-gray-400 ml-3 flex justify-between">
                        <span>Phone Number</span>
                        {errors.phone && <span className="text-red-500">{errors.phone}</span>}
                      </label>
                      <div className="flex">
                        <span className="bg-gray-100 rounded-l-xl px-4 py-4 text-xs font-bold text-gray-500 flex items-center border-r border-gray-200">
                          🇮🇳 +91
                        </span>
                        <input 
                          type="tel" 
                          placeholder="Phone number"
                          value={form.phone}
                          onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                          className={`w-full px-4 py-4 rounded-r-xl bg-gray-50 border-none transition-all font-medium placeholder:text-gray-300 text-sm outline-none ${
                            errors.phone ? 'ring-2 ring-red-400' : 'focus:ring-2 focus:ring-orange-main/20'
                          }`}
                        />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[9px] font-black uppercase tracking-widest text-gray-400 ml-3 flex justify-between">
                        <span>City</span>
                        {errors.city && <span className="text-red-500">{errors.city}</span>}
                      </label>
                      <input 
                        type="text" 
                        placeholder="Your city"
                        value={form.city}
                        onChange={(e) => setForm({ ...form, city: e.target.value })}
                        className={`w-full px-6 py-4 rounded-xl bg-gray-50 border-none transition-all font-medium placeholder:text-gray-300 text-sm outline-none ${
                          errors.city ? 'ring-2 ring-red-400' : 'focus:ring-2 focus:ring-orange-main/20'
                        }`}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-[9px] font-black uppercase tracking-widest text-gray-400 ml-3 flex justify-between">
                        <span>No. of Travelers</span>
                        {errors.travelers && <span className="text-red-500">{errors.travelers}</span>}
                      </label>
                      <input 
                        type="number" 
                        placeholder="Travelers count"
                        value={form.travelers}
                        min="1"
                        onChange={(e) => setForm({ ...form, travelers: e.target.value })}
                        className={`w-full px-6 py-4 rounded-xl bg-gray-50 border-none transition-all font-medium placeholder:text-gray-300 text-sm outline-none ${
                          errors.travelers ? 'ring-2 ring-red-400' : 'focus:ring-2 focus:ring-orange-main/20'
                        }`}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[9px] font-black uppercase tracking-widest text-gray-400 ml-3 flex justify-between">
                        <span>Arrival Date</span>
                        {errors.arrivalDate && <span className="text-red-500">{errors.arrivalDate}</span>}
                      </label>
                      <input 
                        type="date" 
                        min={today}
                        value={form.arrivalDate}
                        onChange={(e) => setForm({ ...form, arrivalDate: e.target.value })}
                        className={`w-full px-6 py-4 rounded-xl bg-gray-50 border-none transition-all font-medium text-gray-700 text-sm outline-none ${
                          errors.arrivalDate ? 'ring-2 ring-red-400' : 'focus:ring-2 focus:ring-orange-main/20'
                        }`}
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[9px] font-black uppercase tracking-widest text-gray-400 ml-3 flex justify-between">
                      <span>Tour Requirements</span>
                      {errors.requirements && <span className="text-red-500">{errors.requirements}</span>}
                    </label>
                    <textarea 
                      rows="3"
                      placeholder="Tell us your requirements"
                      value={form.requirements}
                      onChange={(e) => setForm({ ...form, requirements: e.target.value })}
                      className={`w-full px-6 py-4 rounded-xl bg-gray-50 border-none transition-all font-medium placeholder:text-gray-300 resize-none text-sm outline-none ${
                        errors.requirements ? 'ring-2 ring-red-400' : 'focus:ring-2 focus:ring-orange-main/20'
                      }`}
                    ></textarea>
                  </div>

                  <button 
                    type="submit"
                    disabled={submitting}
                    className="w-full py-4 mt-2 rounded-xl bg-orange-main text-white font-black text-[10px] uppercase tracking-[0.2em] flex items-center justify-center gap-3 hover:bg-orange-600 transition-all duration-500 shadow-xl shadow-black/5 group disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {submitting ? 'Submitting...' : 'Send Inquiry'}
                    {!submitting && <FaArrowRight className="group-hover:translate-x-1.5 transition-transform" />}
                  </button>
                </motion.form>
              ) : (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-16"
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 200, damping: 15, delay: 0.1 }}
                    className="w-24 h-24 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center mx-auto mb-6 shadow-xl shadow-emerald-500/20"
                  >
                    <CheckCircle2 size={48} className="text-white" />
                  </motion.div>
                  <h4 className="text-3xl font-black text-gray-900 mb-3">Thank You!</h4>
                  <p className="text-base text-gray-500 max-w-[280px] mx-auto font-medium leading-relaxed">
                    Your inquiry has been submitted. Our team will contact you shortly to confirm your details.
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default YatraExpertsSection;
